// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Duplicates content to ensure a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #initialChildren = null;
  #ro = null;
  #lastWidth = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });
    this.addEventListener("touchstart", () => this.#animation?.pause(), { signal: this.#ac.signal, passive: true });
    this.addEventListener("touchend", () => this.#animation?.play(), { signal: this.#ac.signal, passive: true });

    this.#ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width || this.offsetWidth;
      if (Math.abs(width - this.#lastWidth) > 5) {
        this.#lastWidth = width;
        this.#setup();
      }
    });
    this.#ro.observe(this);

    if (document.fonts?.ready) {
      document.fonts.ready.then(() => this.#setup());
    } else {
      this.#setup();
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#ro?.disconnect();
    this.#animation?.cancel();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    if (!this.#initialChildren) {
      this.#initialChildren = [...track.children];
    }
    if (this.#initialChildren.length === 0) return;

    // Reset track to initial children
    track.replaceChildren(...this.#initialChildren.map((n) => n.cloneNode(true)));

    if (this.#mql.matches) return;

    // Repeat children until the base sequence is at least as wide as the viewport
    const viewportWidth = Math.max(this.offsetWidth || 0, window.innerWidth || 0);
    let guard = 0;
    while (track.scrollWidth < viewportWidth && guard < 10) {
      track.append(...this.#initialChildren.map((n) => n.cloneNode(true)));
      guard++;
    }

    const cycleCount = track.children.length;
    // Duplicate the entire sequence once for seamless loop
    track.append(...[...track.children].map((n) => n.cloneNode(true)));

    const first = track.children[0];
    const loopPoint = track.children[cycleCount];
    let distance = 0;
    if (first && loopPoint) {
      distance = loopPoint.offsetLeft - first.offsetLeft;
    }
    if (distance <= 0) {
      distance = track.scrollWidth / 2;
    }
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
